import styled, { css } from "styled-components";
import React from "react";

export type TypographyVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "body1" | "body2" | "caption" | "subtitle1" | "subtitle2" | "button" | "inherit";
export type TypographyTextAlign = "left" | "center" | "right" | "justify" | "start" | "end";

export interface TypographyProps extends React.HTMLAttributes<HTMLParagraphElement> {
    variant?: TypographyVariant;
    textAlign?: TypographyTextAlign;
    fontWeight?: number | string;
    component?: keyof JSX.IntrinsicElements;
    textTransform?: "none" | "capitalize" | "uppercase" | "lowercase";
    singleLine?: boolean;
}

const getVariantStyles = (variant?: TypographyVariant) => {
    switch (variant) {
        case "h1":
            return css`
                font-size: 3rem;
                font-weight: 700;
            `;
        case "h2":
            return css`
                font-size: 2rem;
                font-weight: 700;
            `;
        case "h3":
            return css`
                font-size: 1.5rem;
                font-weight: 600;
            `;
        case "h4":
            return css`
                font-size: 1.25rem;
                font-weight: 600;
            `;
        case "h5":
            return css`
                font-size: 1.125rem;
                font-weight: 600;
            `;
        case "h6":
            return css`
                font-size: 1rem;
                font-weight: 600;
            `;
        case "body1":
            return css`
                font-size: 1rem;
                font-weight: 400;
            `;
        case "body2":
            return css`
                font-size: 0.875rem;
                font-weight: 400;
            `;
        case "caption":
            return css`
                font-size: 0.75rem;
                font-weight: 400;
            `;
        case "subtitle1":
            return css`
                font-size: 1rem;
                font-weight: 400;
            `;
        case "subtitle2":
            return css`
                font-size: 0.875rem;
                font-weight: 400;
            `;
        case "button":
            return css`
                font-size: 0.875rem;
                font-weight: 500;
                text-transform: uppercase;
            `;
        case "inherit":
            return css`
                font-size: inherit;
                font-weight: inherit;
            `;
        default:
            return css`
                font-size: 1rem;
                font-weight: 400;
            `;
    }
};

const TypographyRoot = styled.p<TypographyProps>`
    margin: 0;
    padding: 0;
    ${({ variant }) => getVariantStyles(variant)}
    ${({ textAlign }) =>
        textAlign &&
        css`
            text-align: ${textAlign};
        `}
    ${({ fontWeight }) =>
        fontWeight !== undefined &&
        css`
            font-weight: ${fontWeight};
        `}
    ${({ textTransform }) =>
        textTransform &&
        css`
            text-transform: ${textTransform};
        `}
    ${({ singleLine }) =>
        singleLine &&
        css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `}
`;

const Typography: React.FC<TypographyProps> = ({ component, children, variant, ...props }) => {
    // Автоматично визначаємо component на основі variant, якщо component не вказано
    const getComponent = (): keyof JSX.IntrinsicElements => {
        if (component) return component;
        if (variant?.startsWith("h")) return variant as keyof JSX.IntrinsicElements;
        return "p";
    };

    return (
        <TypographyRoot as={getComponent()} {...props} variant={variant}>
            {children}
        </TypographyRoot>
    );
};

export default Typography;


